import React, { useMemo, useState } from "react";
import moment from "moment-timezone";
import {
  Stack,
  Skeleton,
  Typography,
  TextField,
  Autocomplete
} from "@mui/material";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import { useTranslation } from "react-i18next";

import { UiStateType, useSchedulerStore } from "../../state/scheduler.store";
import { TZ } from "../../utils/timezones";
import { SubmittedAvailabilities } from "./SubmittedAvailabilities";
import { IAvailabilityDocData } from "../../services/api/types/roostermeeting.type";
import { formatDurationForMeetingDescription } from "../../utils/duration";
import { useScreenSize } from "../../hooks/useScreenSize";
import { useI18nLanguage } from "../../hooks/i18nLanguage";

interface IMeetingAvailabilityInfoProps {
  uiStep: UiStateType["currentStep"];
  clientLogo?: string;
  meetingDuration: number | null;
  selectedTimezone: string | null;
  submittedAvailabilities: IAvailabilityDocData["availability"];
  submittedAvailabilitiesTz: IAvailabilityDocData["clientTimeZone"];
  hidesubmittedAvailabilities: boolean;
  handleTimezoneChange: (tz: string) => void;
  renderControls: React.ReactNode;
}

export const MeetingAvailabilityInfo: React.FC<
  IMeetingAvailabilityInfoProps
> = ({
  uiStep,
  clientLogo,
  meetingDuration,
  submittedAvailabilities,
  submittedAvailabilitiesTz,
  selectedTimezone,
  hidesubmittedAvailabilities,
  handleTimezoneChange,
  renderControls
}) => {
  const { selectedLanguageForMoment } = useI18nLanguage();
  const { meetingLink } = useSchedulerStore();
  const { isSmallScreen, isMediumScreen, isLargeScreen } = useScreenSize();
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const { t } = useTranslation();

  const meetingDescription = useMemo(() => {
    const duration = meetingLink?.availability?.duration ?? 0;
    return t("ROOSTER.PUBLIC-APP.INTERVIEW_DURATION_DESCRIPTION", {
      defaultValue: `Please choose a date and time for your interview lasting ${duration >= 60 ? moment().startOf("day").add(duration, "minutes").format("hh [hour] mm [minutes]") : `${duration} minutes`}.`,
      duration: duration
    });
  }, [meetingLink]);

  return (
    <Stack
      pt={!isLargeScreen ? 4 : 2}
      pb={2}
      height="100%"
      overflow="auto"
      justifyContent={
        uiStep === "loading" || meetingDescription
          ? "space-between"
          : "flex-start"
      }
    >
      <Stack alignItems="flex-start">
        {/* Visually hidden h1 for accessibility - page must have a level-one heading */}
        <Typography
          component="h1"
          className="sr-only"
          sx={{
            position: "absolute",
            width: "1px",
            height: "1px",
            padding: 0,
            margin: "-1px",
            overflow: "hidden",
            clip: "rect(0, 0, 0, 0)",
            whiteSpace: "nowrap",
            border: 0
          }}
        >
          {t("ROOSTER.PUBLIC-APP.SUBMIT_AVAILABILITY_HEADING", {
            defaultValue: "Submit Your Availability"
          })}
        </Typography>
        <div style={{ height: 48, margin: "2px 0px 4px 0px" }}>
          {!isImageLoaded && (
            <Skeleton variant="rounded" height={48} width={170} />
          )}
          {clientLogo && (
            <img
              alt={t("ROOSTER.PUBLIC-APP.CLIENT_LOGO", {
                defaultValue: "client logo"
              })}
              onError={() => {
                setIsImageLoaded(false);
              }}
              onLoad={() => {
                setIsImageLoaded(true);
              }}
              src={clientLogo}
              style={{
                height: 48,
                maxWidth: 180,
                objectFit: "contain",
                display: isImageLoaded ? "block" : "none"
              }}
            />
          )}
        </div>
        <Stack sx={{ marginTop: 1 }}>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: "bold",
              wordBreak: "break-word",
              maxWidth: "320px"
            }}
          >
            {uiStep !== "loading" ? null : (
              <Skeleton variant="text" height={22} width={"160px"} />
            )}
          </Typography>
          <Typography
            sx={{
              marginTop: 1,
              marginBottom: 1,
              maxWidth: isSmallScreen || isMediumScreen ? "100%" : "320px"
            }}
          >
            {uiStep !== "loading" ? (
              <Stack
                maxHeight={"172px"}
                overflow="auto"
                sx={{ whiteSpace: "pre-wrap" }}
              >
                {t("ROOSTER.PUBLIC-APP.CHOOSE_AVAILABLE_TIMES", {
                  defaultValue:
                    "Please choose available times that are at least lasting"
                })}{" "}
                {formatDurationForMeetingDescription(
                  meetingDuration ?? 0,
                  t,
                  selectedLanguageForMoment
                )}{" "}
                {t("ROOSTER.PUBLIC-APP.EACH", {
                  defaultValue: "each"
                })}
                .
              </Stack>
            ) : (
              <>
                <Skeleton variant="text" height={18} width={"140px"} />
                <Skeleton variant="text" height={18} width={"280px"} />
                <Skeleton variant="text" height={22} width={"280px"} />
                <Skeleton variant="text" height={22} width={"280px"} />
                <Skeleton variant="text" height={22} width={"100px"} />
              </>
            )}
          </Typography>
        </Stack>
      </Stack>

      {uiStep !== "loading" ? (
        <>
          {!hidesubmittedAvailabilities && (
            <Stack direction="column" spacing={0} borderRadius={"5px"}>
              <SubmittedAvailabilities
                submittedAvailabilities={submittedAvailabilities}
                availabilityTimezone={submittedAvailabilitiesTz}
              />
            </Stack>
          )}
        </>
      ) : (
        <>
          <Skeleton variant="text" height={25} width={"85%"} />
          <Skeleton variant="text" height={25} width={"45%"} />
        </>
      )}

      <Stack
        direction="row"
        sx={{
          marginTop: isLargeScreen ? "20px" : null,
          width: "100%",
          justifyContent: "space-between"
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: isLargeScreen ? "flex-start" : "center",
            width: "100%"
          }}
        >
          {!isSmallScreen &&
            (uiStep !== "loading" ? (
              <PublicOutlinedIcon />
            ) : (
              <Skeleton variant="text" height={45} width={30} />
            ))}
          <Stack
            direction={isSmallScreen || isMediumScreen ? "row" : "column"}
            sx={{
              width: "100%",
              gap: "12px",
              alignItems: isLargeScreen ? "flex-start" : "center"
            }}
          >
            {!isSmallScreen && (
              <Typography fontSize="inherit" fontWeight={450}>
                {uiStep !== "loading" ? (
                  t("ROOSTER.PUBLIC-APP.TIMEZONE", {
                    defaultValue: "Timezone"
                  })
                ) : (
                  <Skeleton variant="text" height={20} width={100} />
                )}
              </Typography>
            )}
            {uiStep !== "loading" ? (
              <Autocomplete
                options={TZ}
                value={selectedTimezone ?? moment.tz.guess()}
                onChange={(_, newValue) => {
                  if (newValue) {
                    handleTimezoneChange(newValue);
                  }
                }}
                disableClearable
                getOptionLabel={(option) => {
                  const offset = moment.tz(option).format("Z");
                  return `(${offset}) ${option}`;
                }}
                slotProps={{
                  popper: {
                    disablePortal: true,
                    sx: {
                      minWidth: "300px",
                      width: "100%",
                      border: "1px solid #e0e0e0",
                      borderRadius: "4px",
                      overflow: "ellipsis",
                      "& .MuiAutocomplete-listbox": {
                        "&::-webkit-scrollbar": {
                          width: "4px",
                          background: "transparent",
                          borderRadius: "10px",
                          paddingRight: "4px !important"
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "transparent",
                          backgroundClip: "padding-box",
                          borderRadius: "10px"
                        },
                        "&:hover": {
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "#888",
                            backgroundClip: "padding-box"
                          },
                          "&::-webkit-scrollbar-thumb:hover": {
                            backgroundColor: "#555"
                          }
                        }
                      }
                    }
                  }
                }}
                sx={{
                  width: "100%",
                  minWidth: "150px",
                  maxWidth: "100%"
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputProps={{
                      ...params.inputProps,
                      "aria-label": t("ROOSTER.PUBLIC-APP.SELECT_TIMEZONE", {
                        defaultValue: "Select timezone"
                      })
                    }}
                    sx={{
                      ".MuiAutocomplete-inputRoot": {
                        padding: "0px 6px",
                        width: "100%",
                        maxWidth: "280px"
                      }
                    }}
                  />
                )}
                disabled={uiStep !== "initial"}
              />
            ) : (
              <Skeleton variant="text" height={50} width={170} />
            )}
          </Stack>
        </Stack>
        <Stack>{renderControls}</Stack>
      </Stack>
    </Stack>
  );
};
